import React, { useEffect, useRef, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { BsWindowSidebar } from 'react-icons/bs';
import { LuFlaskConical } from 'react-icons/lu';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { fetchUserProjects } from '../shared/api/integration';
import { SANDBOX_EXPIRES_KEY } from '../shared/api/sandbox';
import { AIChatDrawerPosition, AIChatProvider, useAIChat } from '../shared/contexts/AIChat.context';
import { ProjectsProvider } from '../shared/contexts/Projects.provider';
import { ThemeProvider } from '../shared/contexts/Theme.context';
import { auth } from '../shared/firebase/firebase';
import useSocket from '../shared/hooks/socket-hook';
import { useMediaQuery } from '../shared/hooks/use-media-query';
import { AI_CHAT_DRAWER_HEIGHT, AI_CHAT_DRAWER_WIDTH, AIChatDrawer } from './dashboard/AIChatDrawer';
import { ProactiveInsights } from './dashboard/ProactiveInsights';
import OnboardingFlow from './OnboardingFlow';
import Slidebar from './Slidebar.v2';

const SIDEBAR_COLLAPSED_WIDTH = 80;
const SIDEBAR_EXPANDED_WIDTH = 288;

const formatCountdown = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const Layout: React.FC = () => {
  const [projects, setProjects] = useState<any[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [pendingJiraCount, setPendingJiraCount] = useState(0);

  useEffect(() => {
    fetchUserProjects()
      .then((d) => {
        const selectedSet = new Set(d.selectedProjectIds);
        // `mapId` is the internal Project doc's Mongo _id — the id every dashboard endpoint
        // actually expects (not Jira's raw project id). It can be temporarily unmapped on the
        // backend (e.g. right after selecting a brand-new project, before its first sync
        // resolves the mapping) — exclude those rather than pass an id-less project through:
        // an <option> with no value falls back to its text (the project name) in the DOM, which
        // is exactly the "project name ends up in the URL" bug this guards against.
        const selectedJiraProjects = d.availableProjects.filter((p) => selectedSet.has(p.id));
        const jiraProjects = selectedJiraProjects
          .filter((p) => !!p.mapId)
          .map((p) => ({
            _id: p.mapId!,
            projectId: p.id,
            name: p.name,
            key: p.key,
            mapId: p.mapId,
            provider: 'jira' as const,
          }));
        setPendingJiraCount(selectedJiraProjects.length - jiraProjects.length);
        const trelloProjects = (d.trelloBoards ?? []).map((b) => ({
          _id: b.id,
          projectId: b.id,
          name: b.name,
          provider: 'trello' as const,
        }));
        const standaloneProjects = (d.standaloneProjects ?? []).map((p) => ({
          _id: p._id,
          projectId: p._id,
          name: p.name,
          provider: 'standalone' as const,
        }));
        setProjects([...jiraProjects, ...trelloProjects, ...standaloneProjects]);
      })
      .finally(() => setProjectsLoading(false));
  }, []);

  return (
    <ThemeProvider>
      <AIChatProvider>
        <ProjectsProvider value={projects} isLoading={projectsLoading} pendingJiraCount={pendingJiraCount}>
          <LayoutShell />
        </ProjectsProvider>
      </AIChatProvider>
    </ThemeProvider>
  );
};

const LayoutShell: React.FC = () => {
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isOpen, isPinned, position } = useAIChat();
  useSocket(auth.currentUser?.uid || ''); // just for connection on the top level
  const isSandbox = auth.currentUser?.isAnonymous ?? false;
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const bannerRef = useRef<HTMLDivElement>(null);
  const [bannerHeight, setBannerHeight] = useState(0);
  const topOffset = isSandbox ? bannerHeight : 0;

  const sidebarWidth = isDesktop ? (sidebarOpened ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH) : 0;
  const isDocked = isPinned && isOpen;
  const dockedPosition: AIChatDrawerPosition = isDocked && !isDesktop ? 'bottom' : position;

  // Measure the badge instead of assuming a fixed height — its text wraps to
  // two lines on narrow screens, and the countdown text length varies.
  useEffect(() => {
    if (!isSandbox || !bannerRef.current) return;
    const el = bannerRef.current;
    const observer = new ResizeObserver(([entry]) => setBannerHeight(Math.ceil(entry.contentRect.height)));
    observer.observe(el);
    return () => observer.disconnect();
  }, [isSandbox]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('slack') === 'connected') {
      toast.success('Slack connected!');
      params.delete('slack');
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [location.search, navigate]);

  const workspaceStyle: React.CSSProperties = {
    paddingLeft: isDocked && dockedPosition === 'left' ? AI_CHAT_DRAWER_WIDTH : 0,
    paddingRight: isDocked && dockedPosition === 'right' ? AI_CHAT_DRAWER_WIDTH : 0,
    paddingBottom: isDocked && dockedPosition === 'bottom' ? AI_CHAT_DRAWER_HEIGHT : 0,
    transition: 'padding-left 300ms ease, padding-right 300ms ease, padding-bottom 300ms ease',
  };

  return (
    <div className="h-screen bg-base-200">
      {/* Sandbox mode badge — spans the full width, above everything else */}
      {isSandbox && <SandboxBanner bannerRef={bannerRef} />}

      {/* Mobile top bar — visible only on small screens */}
      <div
        className="md:hidden fixed left-0 right-0 z-50 h-14 bg-base-100 border-b border-base-300 flex items-center px-4 gap-3"
        style={{ top: topOffset }}
      >
        <button className="btn btn-ghost btn-sm" onClick={() => setSidebarOpened(true)} title="Open menu">
          <BsWindowSidebar className="text-lg" />
        </button>
        <span className="font-display font-bold">MFlow</span>
      </div>

      {/* Backdrop overlay — mobile only, when sidebar is open */}
      {sidebarOpened && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
        <div className="md:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setSidebarOpened(false)} />
      )}

      <div className="flex h-full" style={workspaceStyle}>
        <Slidebar isOpen={sidebarOpened} onToggle={setSidebarOpened} topOffset={topOffset} />

        <main
          className={[
            'flex-1 min-w-0 min-h-0 overflow-y-auto',
            'pt-14 md:pt-0', // offset for mobile top bar
            sidebarOpened ? 'md:ml-72' : 'md:ml-20',
            // scoped to margin-left only — marginTop below is a one-time layout
            // measurement, not something that should visibly animate in
            'transition-[margin-left] duration-300',
          ].join(' ')}
          style={{ marginTop: topOffset }} // offset for the sandbox badge, if present
        >
          <div className="min-h-full">
            <Outlet />
          </div>
        </main>
      </div>
      <AIChatDrawer sidebarWidth={sidebarWidth} topOffset={topOffset} isDesktop={isDesktop} />
      <ProactiveInsights />
      <OnboardingFlow />
      <Toaster position="bottom-right" />
    </div>
  );
};

const SandboxBanner = ({ bannerRef }: { bannerRef: React.RefObject<HTMLDivElement | null> }) => {
  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  useEffect(() => {
    const expiresAtRaw = localStorage.getItem(SANDBOX_EXPIRES_KEY);
    const expiresAt = expiresAtRaw ? new Date(expiresAtRaw).getTime() : NaN;
    if (Number.isNaN(expiresAt)) return;
    const tick = () => setRemainingMs(Math.max(0, expiresAt - Date.now()));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      ref={bannerRef}
      className="fixed top-0 left-0 right-0 z-[55] bg-warning text-warning-content flex items-center justify-center gap-2 px-4 py-1.5 text-xs sm:text-sm font-semibold text-center"
    >
      <LuFlaskConical className="shrink-0 text-base" />
      <span>
        Sandbox Mode
        {remainingMs != null && (remainingMs > 0 ? ` — expires in ${formatCountdown(remainingMs)}` : ' — expired')}
      </span>
    </div>
  );
};

export default Layout;
